import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { Camera } from 'shared/components/Icons';
import { getError, getUserLocale } from 'utils/appHelpers';
import ManageModal from '../../modals/ManageModal';
import Input from '../../Input';
import ProjectCheckboxSelection from './../ProjectCheckboxSelection';
import ProjectModal from 'app/routes/Dashboard/routes/Home/components/ProjectModal';
import WebCamera from './WebCamera';
import PhotoButton from './PhotoButton';
import useDebounce from 'shared/hooks/useDebounce';
import ImageCropper from './ImageCropper';
import { createProduct } from 'app/routes/Dashboard/actions/products';
import { toggleProjectFromLikiee } from 'app/routes/Dashboard/actions/projects';
import { scaleDownImage } from 'app/routes/Dashboard/routes/Account/routes/ProfileEdit/components/utils';

const AddProductByImageModal = ({ onClose, customProjectID }) => {
  const { t } = useTranslation();
  const [showCamera, setShowCamera] = useState(false);
  const alert = useAlert();
  const [croppedImg, setCroppedImg] = useState();
  const [imageToCrop, setImageToCrop] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);
  const [projectIDs, setProjectIds] = useState(customProjectID ? [customProjectID] : []);
  const [showProjectCreation, setShowProjectCreation] = useState(false);
  const debUrl = useDebounce(url, 300);

  const onSetImageToCrop = async imgUrl => {
    const newUrl = await scaleDownImage(imgUrl, 500, 240);
    setImageToCrop(newUrl);
  };

  const checkImage = async url => {
    if (!url) {
      setError(false);
      return;
    }
    setFetching(true);
    const img = new Image();
    img.onload = function() {
      setError(false);
      setFetching(false);
      onSetImageToCrop(url);
    };
    img.onerror = function() {
      setFetching(false);
      setError(t('error_invalid_url'));
    };
    img.src = url;
  };

  const onFormSubmit = async () => {
    try {
      setFetching(true);
      const locale = getUserLocale();
      const product = await createProduct({ locale, imageURL: croppedImg });
      await toggleProjectFromLikiee(product.id, projectIDs);
      onClose();
      setFetching(false);
      alert.success(t('msg_success_product_create'));
    } catch (err) {
      setFetching(false);
      alert.error(getError(err));
    }
  };

  const onCloseProjectCreation = () => {
    setShowProjectCreation(false);
  };

  const handleClickCamera = () => {
    setShowCamera(true);
  };

  const onWebcamPhotoChoose = imgUrl => {
    setShowCamera(false);
    onSetImageToCrop(imgUrl);
  };

  const onSelectPhoto = e => {
    if (!e.target.files || !e.target.files[0]) return;
    onSetImageToCrop(URL.createObjectURL(e.target.files[0]));
  };

  useEffect(() => {
    checkImage(debUrl);
  }, [debUrl]);

  if (imageToCrop) {
    return (
      <ImageCropper
        imgUrl={imageToCrop}
        onClose={() => {
          setImageToCrop(false);
          setUrl('');
        }}
        onSuccess={img => setCroppedImg(img)}
      />
    );
  }

  if (showProjectCreation) {
    return <ProjectModal onClose={onCloseProjectCreation} />;
  }

  return (
    <ManageModal
      title={t('add_product_photo_title')}
      sumbitButton={t('btn_save')}
      onCloseModal={showCamera ? setShowCamera.bind(null, false) : onClose}
      onSubmit={showCamera ? null : onFormSubmit}
      className='alert-modal'
      isButtonDisabled={fetching}
    >
      {!showCamera && (
        <>
          {!croppedImg && (
            <div className='col-12 p-0 mb-12 mb-md-15'>
              <Input
                type='text'
                name='name'
                onChange={({ target }) => setUrl(target.value)}
                value={url}
                label={t('label_insert_image_url')}
                placeholder=''
                errorText={error}
                readOnly={fetching}
              />
              <div className='mb-8 text-center text-or'>
                <span className='text-black-50'>{t('text_or')}</span>
              </div>
              <div className='form-block d-flex flex-wrap row px-md-10'>
                <div className='pr-md-8 col-md-6 col-12 pl-md-0'>
                  <input
                    type='file'
                    className='d-none'
                    id='fileSelect'
                    accept='image/png, image/gif, image/jpeg'
                    name='fileSelect'
                    onChange={onSelectPhoto}
                  />
                  <label
                    htmlFor='fileSelect'
                    className='btn btn-block py-7 bg-gray-light font-weight-normal d-flex align-items-center text-nowrap justify-content-center'
                  >
                    <Camera className='mr-5' />
                    {t('photo_option_choose')}
                  </label>
                </div>
                <div className='pl-md-8 col-md-6 col-12 pr-md-0 d-none d-md-block'>
                  <PhotoButton text={t('photo_option_take')} icon='picture' onClick={handleClickCamera} />
                </div>
              </div>
            </div>
          )}
          {croppedImg && (
            <div className='d-flex justify-content-center mb-10'>
              <img src={croppedImg || ''} alt='preview' className='rounded-10' />
            </div>
          )}
          <div className='row pb-5'>
            <ProjectCheckboxSelection
              setShowProjectCreation={setShowProjectCreation}
              projectIDs={projectIDs}
              setProjectIds={setProjectIds}
            />
          </div>
        </>
      )}
      {showCamera && <WebCamera onPhotoChoose={onWebcamPhotoChoose} />}
    </ManageModal>
  );
};

export default AddProductByImageModal;
